import {findDOMNode} from 'react-dom';

import debugHook from './hookUtils';
import useResizeObserver from './useResizeObserver';

export default debugHook(useContainerQuery, 'useContainerQuery');

/**
 * Hook that returns list of matching width/height breakpoints which the element at `ref` is
 * greater than or equal to
 *
 * Width/height dimensions can be given in pixels (without a unit), or CSS units (e.g. vw, vh, rem, ch, etc)
 *
 * @param {object} props
 * @param {Array<number|string>} props.widthBreaks - dimensions of widths you are interested in
 * @param {Array<number|string>} props.heightBreaks - dimensions of heights you are interested in
 * @returns {{widths: Array<number|string>, height: Array<number|string>, ref: React.MutableRefObject}}
 */
export function useContainerQuery({widthBreaks = [], heightBreaks = []} = {}) {
    const { width, height, ref } = useResizeObserver({ ref });

    let widths = {};
    widthBreaks.forEach(unit => {
        widths[unit] = width <= getPixels(ref.current, unit);
    });

    let heights = {};
    heightBreaks.forEach(unit => {
        heights[unit] = height <= getPixels(ref.current, unit);
    });

    return {widths, heights, ref };
}

/**
 * Helper to get convert a CSS unit into pixels
 *
 * @param {HTMLElement} el
 * @param {String|number} width
 */
function getPixels(el, width) {
    const maybePixels = Number(width);
    if (!Number.isNaN(maybePixels)) {
        return maybePixels;
    }

    if (!el) {
        return null;
    }

    const node = findDOMNode(el);

    // A 'test' element is required for measurement
    let test = document.createElement('div');
    Object.assign(test.style, {
        // Absolute positioning to free element for resizing
        // and avoid potential jumps/jank
        position: 'absolute',
        width,
    });
    node.appendChild(test);
    // The value needed is offsetWidth
    let pixels = test.offsetWidth;
    node.removeChild(test);
    return pixels;
}
